import { FC, Fragment } from "react";
import { TableCell, TableRow } from "@mui/material";
import { merge } from "lodash";
import { GenericTableRow, LabelValueItem } from "./generic-table";
import { makeSxStyles } from "ui";

interface GenericTableRowsProps {
  renderedRows: GenericTableRow[];
  headers: LabelValueItem[];
  onRowClick?: (data: any, e: React.MouseEvent) => void;
  activeRow: number | null;
  setActiveRow?: React.Dispatch<React.SetStateAction<number | null>>;
  draggable: boolean;
  tableWidth?: string;
}

const GenericTableRows: FC<GenericTableRowsProps> = ({
  renderedRows,
  onRowClick,
  activeRow,
  setActiveRow,
  tableWidth,
}) => {
  const styles = useStyles();
  return renderedRows.map((row, index) => (
    <Fragment key={index}>
      <TableRow
        sx={merge(
          styles.tableRow,
          onRowClick ? styles.tableRowClickable : {},
          tableWidth ? { width: tableWidth, minWidth: tableWidth } : {},
        )}
        key={index}
        hover
        selected={activeRow === index}
        onClick={(e) => {
          onRowClick?.(row.item, e);
          setActiveRow?.(index);
        }}
      >
        {row.values.map(({ label, value, onCellClick, customCSS }) => (
          <TableCell
            key={value}
            component="th"
            scope="row"
            style={customCSS}
            onClick={() => onCellClick?.(value)}
            align={value === "notifications" ? "center" : "left"}
            sx={{
              paddingLeft: "24px",
              paddingRight: "24px",
            }}
          >
            {label}
          </TableCell>
        ))}
      </TableRow>
    </Fragment>
  ));
};

const useStyles = makeSxStyles(({ palette: { grey } }) => ({
  tableRow: {
    height: "70px",
    background: grey["800"],
    // Keep the hover actions in the layout at all times (space reserved),
    // toggling only visibility on hover. Using display:none/flex here would
    // change the cell's width on hover and reflow every column to the right.
    ".toShow": {
      opacity: 0,
      visibility: "hidden",
      display: "flex",
    },
    "&:hover": {
      ".toHide": {
        display: "none",
      },
      ".toShow": {
        opacity: 1,
        visibility: "visible",
        display: "flex",
      },
    },
    "&:visited": {
      outline: "1px solid #fff",
    },
  },
  tableRowClickable: {
    "&:hover": {
      cursor: "pointer",
    },
  },
}));

export default GenericTableRows;
